import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import {
  GridRowModes,
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
  GridRowEditStopReasons
} from "@mui/x-data-grid";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import {
  Alert,
  AppBar,
  Card,
  Chip,
  experimentalStyled,
  IconButton,
  MenuItem,
  Select,
  Typography,
  FormControl,
  InputLabel,
  CardContent,
  Paper,
  Tooltip
} from "@mui/material";
import {
  FirstPage,
  LastPage,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import {GET, PATCH, BASE_URL, POST} from "APIclient";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import properties from "constants/ApplicationConstants";
import {formatDate} from "constants/ApplicationConstants";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault(dayjs.tz.guess());

const roles = ["Market", "Finance", "Development"];

// Custom Pagination Component
function CustomPagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading,
}) {
  const handleFirstPage = () => onPageChange(0);
  const handlePreviousPage = () => onPageChange(Math.max(0, page - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages - 1, page + 1));
  const handleLastPage = () => onGoToLastPage();

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        borderTop: "1px solid #e0e0e0",
      }}
    >
      <Typography variant="caption" sx={{ color: "#666" }}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "No rows"}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" sx={{ mr: 2, color: "#666" }}>
          Page {page + 1} of {totalPages || 1}
        </Typography>

        <Tooltip title="First Page">
          <IconButton
            onClick={handleFirstPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <FirstPage />
          </IconButton>
        </Tooltip>

        <Tooltip title="Previous Page">
          <IconButton
            onClick={handlePreviousPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <NavigateBefore />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>

        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor:
                  properties?.colors?.bgcolorbluethem || "#1565c0",
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function ProjectList() {
  const [inputs, setInputs] = React.useState({
    projectId: "",
    projectName: "",
    projectType: "",
    duration: "",
    clientId: "",
    clientName: "",
    startDate: null,
    completeDate: null
  });
  const [data, setData] = React.useState([]);
  const [filteredData, setFilteredData] = React.useState([]);
  const [rowModesModel, setRowModesModel] = React.useState({});
  const empId = window.sessionStorage.getItem("empId");
  const addUrl = "admin/project/add";
  const [open, setOpen] = React.useState(false);
  const [newId, setNewId] = React.useState();
  const [snakColor, setSnakColor] = React.useState("");
  const [snakMessage, setSnakMessage] = React.useState("");
  const [snackbar, setSnackbar] = React.useState(null);
  const [clients, setClients] = React.useState([]);
  const [projects, setProjects] = React.useState([]);
  const [projectsForRow, setProjectsForRow] = React.useState({});
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(20);
  const [totalRows, setTotalRows] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [loading, setLoading] = React.useState(false);

  const [searchParams, setSearchParams] = React.useState({
    projectName: "",
    clientCode: "",
    projectType: ""
  });

  React.useEffect(() => {
    const fetchClient = async () => {
      try {
        const clientResponse = await GET("admin/client-list?page=0&size=20");
        if (clientResponse?.status === 200) {
          const data = clientResponse?.data?.data?.paginatedData;
          setClients(data);
        }
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
    };
    fetchClient();
  }, []);

  const fetchData = async (page, pageSize, searchParams = {}, signal) => {
    try {
      setLoading(true);
      const params = {
        projectName: searchParams.projectName || null,
        clientCode: searchParams.clientCode || null,
        projectType: searchParams.projectType ? (searchParams.projectType === "1" ? 1 : 2) : null,
        page,
        size: pageSize
      };

      const queryString = Object.keys(params)
        .filter(key => params[key] !== null)
        .map(key => `${key}=${encodeURIComponent(params[key])}`)
        .join("&");

      const fetchUrl = `admin/project/index?${queryString}`;
      const response = await GET(fetchUrl, { signal });
      console.log("API Response:", response);

      if (response.status === 200) {
        const data = response?.data?.data?.paginatedData || [];
        const totalRecords = response?.data?.data?.totalItems || 0;
        const totalPages = response?.data?.data?.totalPages || 0;

        const formattedData = data.map((item, index) => ({
          id: page * pageSize + index + 1,
          projectId: item?.projectId,
          projectName: item?.projectName,
          projectType: item?.projectType,
          clientId: item?.clientId,
          clientCode: item?.clientCode,
          clientName: item?.clientName,
          startDate: item?.startDate,
          completeDate: item?.completeDate,
          status: item?.status
        }));

        setData(formattedData);
        setFilteredData(formattedData);
        setTotalRows(totalRecords);
        setTotalPages(totalPages);
        console.log("Total Rows:", totalRecords, "Total Pages:", totalPages);

        if (data && data.length) {
          setNewId(data[data.length - 1].projectId + 1);
        } else {
          setNewId(1);
        }
      } else {
        console.error("Unexpected response status:", response.status);
        setData([]);
        setFilteredData([]);
        setTotalRows(0);
        setTotalPages(0);
        setSnakColor("error");
        setSnakMessage("Failed to fetch data");
        setOpen(true);
      }
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Error fetching data:", error);
      setData([]);
      setFilteredData([]);
      setTotalRows(0);
      setTotalPages(0);
      setSnakColor("error");
      setSnakMessage("Error fetching data");
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    const controller = new AbortController();
    fetchData(page, pageSize, searchParams, controller.signal);
    return () => controller.abort();
  }, [page, pageSize]);

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleEditClick = id => () => {
    setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.Edit}});
  };

  const handleSaveClick = id => () => {
    setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.View}});
  };

  const handleClientChange = (rowId, selectedClientName) => {
    const selectedClient = clients.find(client => client.clientName === selectedClientName);
    const updatedRows = data.map(row =>
      row.projectId === rowId
        ? {...row, clientId: selectedClient?.clientId, clientName: selectedClientName}
        : row
    );
    setData(updatedRows);
    return updatedRows;
  };

  const handleCancelClick = id => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: {mode: GridRowModes.View, ignoreModifications: true}
    });

    const editedRow = data.find(row => row.projectId === id);
    if (editedRow.isNew) {
      setData(data.filter(row => row.projectId !== id));
    }
  };

  const renderStatusChip = params => {
    return (
      <Chip
        size="small"
        label={params.value == 1 ? "Active" : "Inactive"}
        color={params.value === 1 ? "success" : "error"}
        sx={{
          backgroundColor: params.value === 1 ? "green" : "red",
          color: "#fff",
          fontWeight: "400"
        }}
      />
    );
  };

  const processRowUpdate = async newRow => {
    const updatedRow = {...newRow, isNew: false};
    let dt;
    if (updatedRow.clientCode) {
      dt = {
        projectId: updatedRow.projectId,
        projectName: updatedRow.projectName,
        projectType: updatedRow.projectType === "In-House" ? 1 : 2,
        clientId: updatedRow.clientId,
        startDate: updatedRow.startDate ? dayjs(updatedRow.startDate).format("YYYY-MM-DD") : null,
        completeDate: updatedRow.completeDate
          ? dayjs(updatedRow.completeDate).format("YYYY-MM-DD")
          : null
      };
    } else {
      dt = {
        projectName: updatedRow.projectName,
        projectType: updatedRow.projectType === "In-House" ? 1 : 2,
        clientId: updatedRow.clientId,
        startDate: updatedRow.startDate ? dayjs(updatedRow.startDate).format("YYYY-MM-DD") : null,
        completeDate: updatedRow.completeDate
          ? dayjs(updatedRow.completeDate).format("YYYY-MM-DD")
          : null
      };
    }

    setData(data => data.map(row => (row.projectId === updatedRow.projectId ? updatedRow : row)));

    try {
      const response = await POST(addUrl, dt);
      console.log("POST response:", response);
      setSnakColor("success");
      setSnakMessage("Project details updated successfully");
      setOpen(true);
      await fetchData(page, pageSize, searchParams);
    } catch (err) {
      console.error("Error adding project:", err);
      setSnakColor("error");
      setSnakMessage(err.data?.message || "Error adding project");
      setOpen(true);
    }

    return updatedRow;
  };

  const handleProcessRowUpdateError = React.useCallback(error => {
    setSnackbar({children: error.message, severity: "error"});
  }, []);

  const handleRowModesModelChange = newRowModesModel => {
    setRowModesModel(newRowModesModel);
  };

  const onSearch = event => {
    const {name, value} = event.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = async () => {
    try {
      setPage(0);
      setLoading(true);
      await fetchData(0, pageSize, searchParams);
    } catch (error) {
      console.error("Error fetching search results:", error);
      setSnakColor("error");
      setSnakMessage("Error fetching search results");
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleGoToLastPage = async () => {
    if (totalPages > 0) {
      const lastPageIndex = totalPages - 1;
      setPage(lastPageIndex);
      await fetchData(lastPageIndex, pageSize, searchParams);
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 0);
    }
  };

  const columns = [
    {field: "id", headerName: "#", width: 20, headerAlign: "center", align: "center"},
    {
      field: "projectName",
      headerName: "Project Name",
      width: 120,
      headerAlign: "left",
      editable: true,
      align: "left"
    },
    {
      field: "projectType",
      headerName: "Project Type",
      width: 110,
      align: "left",
      editable: true,
      headerAlign: "left",
      type: "singleSelect",
      valueOptions: ["In-House", "Client-Side"],
      valueGetter: value => {
        if (value == 1) return "In-House";
        if (value == 2) return "Client-Side";
        return "-";
      }
    },
    {
      field: "clientCode",
      headerName: "Client Code",
      width: 100,
      headerAlign: "left",
      align: "left"
    },
    {
      field: "clientName",
      headerName: "Client Name",
      width: 150,
      editable: true,
      type: "singleSelect",
      renderEditCell: params => (
        <Select
          fullWidth
          value={params.value ? params.value : params.row.clientName}
          onChange={event => handleClientChange(params.id, event.target.value)}
        >
          <MenuItem value="">
            <em>Select</em>
          </MenuItem>
          {clients.map(client => (
            <MenuItem key={client.clientId} value={client.clientName}>
              {client.clientName}
            </MenuItem>
          ))}
        </Select>
      ),
      valueGetter: params => {
        const selectedClient = clients.find(client => client.clientName === params);
        return params ? params : selectedClient?.clientName || "-";
      }
    },
    {
      field: "startDate",
      headerName: "Start Date",
      width: 140,
      align: "left",
      editable: true,
      renderCell: params => formatDate(params.value),
      renderEditCell: params => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            format="DD/MM/YYYY"
            value={params.value ? dayjs(params.value) : null}
            onChange={date => {
              params.api.setEditCellValue({id: params.id, field: params.field, value: date});
            }}
          />
        </LocalizationProvider>
      ),
      headerAlign: "left"
    },
    {
      field: "completeDate",
      headerName: "Complete Date",
      width: 170,
      editable: true,
      headerAlign: "left",
      align: "left",
      renderCell: params => formatDate(params.value),
      renderEditCell: params => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            format="DD/MM/YYYY"
            value={params.value ? dayjs(params.value) : null}
            onChange={date => {
              params.api.setEditCellValue({id: params.id, field: params.field, value: date});
            }}
          />
        </LocalizationProvider>
      )
    },
    {
      field: "status",
      headerName: "Status",
      width: 80,
      headerAlign: "left",
      align: "left",
      renderCell: renderStatusChip
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: ({id}) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{color: "primary.main"}}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />
        ];
      }
    }
  ];

  const handleNullValues = data => {
    return data && data.length
      ? data.map(row => ({
          ...row,
          projectName: row.projectName ?? "N/A",
          projectType: row.projectType ?? "N/A",
          clientName: row.clientName ?? " ",
          startDate: row.startDate
            ? dayjs(row.startDate).isValid()
              ? dayjs(row.startDate)
              : null
            : null,
          completeDate: row.completeDate
            ? dayjs(row.completeDate).isValid()
              ? dayjs(row.completeDate)
              : null
            : null
        }))
      : [];
  };

  const cleanedRows = handleNullValues(filteredData);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox container pt={2} pb={2} px={3}>
              <MDBox sx={{display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 3}}>
                <Typography
                  sx={{
                    gridColumn: "span 2",
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "15px",
                    fontWeight: "bold"
                  }}
                >
                  Project List
                </Typography>
                <MDInput
                  sx={{gridColumn: "span 2"}}
                  value={searchParams.clientCode}
                  onChange={onSearch}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  name="clientCode"
                  label="Client Code"
                  variant="outlined"
                />
                <MDInput
                  sx={{gridColumn: "span 2"}}
                  value={searchParams.projectName}
                  onChange={onSearch}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  name="projectName"
                  label="Project Name"
                  variant="outlined"
                />
                <FormControl variant="outlined" sx={{m: 0, width: "20ch", gridColumn: "span 2"}}>
                  <InputLabel id="projectType">Project Type</InputLabel>
                  <Select
                    labelId="projectType"
                    id="projectType"
                    name="projectType"
                    value={searchParams.projectType}
                    onChange={onSearch}
                    onKeyDown={event => {
                      if (event.key === "Enter") {
                        handleSearch();
                      }
                    }}
                    label="Project Type"
                    sx={{minWidth: 150, p: 1.5}}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    <MenuItem value="1">In-House</MenuItem>
                    <MenuItem value="2">Client-Side</MenuItem>
                  </Select>
                </FormControl>
                <MDButton
                  sx={{
                    gridColumn: "span 2",
                    fontFamily: properties.fontFamily,
                    background: properties.colors.bgcolorbluethem
                  }}
                  variant="contained"
                  color="info"
                  onClick={handleSearch}
                >
                  Search
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </AppBar>

        {/* DataGrid with Custom Pagination */}
        <MDBox>
          <Paper>
            <DataGrid
              rows={cleanedRows}
              columns={columns}
              // rowHeight={35}
              editMode="row"
              rowModesModel={rowModesModel}
              getRowId={row => row.projectId}
              columnVisibilityModel={{
                projectId: false
              }}
              size="small"
              paginationMode="server"
              rowCount={totalRows}
              pageSizeOptions={[20]}
              hideFooter
              loading={loading}
              sx={{
                height: "450px",
                fontSize: "14px",
                fontWeight:400,
                fontFamily: properties.fontFamily,
                "& .MuiDataGrid-scrollbar": {
                  width: "10px !important",
                },
                "& .actions": {
                  color: "text.secondary"
                },
                "& .textPrimary": {
                  color: "text.primary"
                }
              }}
              aria-label="a dense table"
              onRowModesModelChange={handleRowModesModelChange}
              onRowEditStop={handleRowEditStop}
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={handleProcessRowUpdateError}
              initialState={{
                sorting: {
                  sortModel: [{field: "projectId", sort: "desc"}]
                }
              }}
            />
            <CustomPagination
              page={page}
              pageSize={pageSize}
              totalRows={totalRows}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onGoToLastPage={handleGoToLastPage}
              loading={loading}
            />
          </Paper>
        </MDBox>

        <Snackbar
          open={open}
          anchorOrigin={{vertical: "top", horizontal: "right"}}
          autoHideDuration={6000}
          onClose={handleClose}
        >
          <Alert
            onClose={handleClose}
            severity={snakColor === "success" ? "success" : "error"}
            variant="filled"
            sx={{width: "100%", color: "#fff"}}
          >
            {snakMessage}
          </Alert>
        </Snackbar>
      </MDBox>
    </DashboardLayout>
  );
}